/**
 * Finance permission vocabulary and the direct-route authorization boundary.
 *
 * Every string here is copied verbatim from `x-required-permission` in the
 * ratified contract (openapi/erp.yaml, mirrored in
 * openapi/generated/route_policy.gen.go). A control the operator may not use
 * is never rendered — a button that is guaranteed to 403 is a bug, not a
 * safety net.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import type { AppCode } from "@/lib/modules";
import { useSession } from "@/lib/session/use-session";

export const FINANCE_PERMISSIONS = {
  // Tax codes
  taxCodeRead: "finance.tax-code.read",
  taxCodeCreate: "finance.tax-code.create",
  taxCodeUpdate: "finance.tax-code.update",
  taxReturnRead: "finance.tax-return.read",

  // Accounts payable
  supplierInvoiceRead: "finance.supplier-invoice.read",
  supplierInvoiceCreate: "finance.supplier-invoice.create",
  supplierInvoiceMatch: "finance.supplier-invoice.match",
  supplierInvoicePost: "finance.supplier-invoice.post",
  supplierInvoiceResolveDiscrepancy: "finance.supplier-invoice.resolve-discrepancy",
  supplierInvoiceOverrideDiscrepancy: "finance.supplier-invoice.override-discrepancy",
  apAgingRead: "finance.ap-aging.read",
  grniRead: "finance.grni.read",

  // Accounts receivable
  customerInvoiceRead: "finance.customer-invoice.read",
  customerInvoiceCreate: "finance.customer-invoice.create",
  customerInvoiceFinalize: "finance.customer-invoice.finalize",
  creditNoteRead: "finance.credit-note.read",
  creditNoteCreate: "finance.credit-note.create",
  creditNoteFinalize: "finance.credit-note.finalize",
  customerReceiptRead: "finance.customer-receipt.read",
  customerReceiptCreate: "finance.customer-receipt.create",
  customerReceiptAllocate: "finance.customer-receipt.allocate",
  arAgingRead: "finance.ar-aging.read",

  // Payment runs
  paymentRunRead: "finance.payment-run.read",
  paymentRunCreate: "finance.payment-run.create",
  paymentRunSubmit: "finance.payment-run.submit",
  paymentRunPost: "finance.payment-run.post",
  paymentRunExport: "finance.payment-run.export",

  // Banking
  bankAccountRead: "finance.bank-account.read",
  bankAccountCreate: "finance.bank-account.create",
  bankAccountUpdate: "finance.bank-account.update",
  bankRuleRead: "finance.bank-rule.read",
  bankRuleConfigure: "finance.bank-rule.configure",
  dunningRead: "finance.dunning.read",
  dunningConfigure: "finance.dunning.configure",
  dunningRun: "finance.dunning.run",
  customerStatementRead: "finance.customer-statement.read",
  customerStatementRun: "finance.customer-statement.run",
  statementRead: "finance.statement.read",
  statementLayoutRead: "finance.statement-layout.read",
  statementLayoutCreate: "finance.statement-layout.create",
  statementLayoutUpdate: "finance.statement-layout.update",
  bankStatementRead: "finance.bank-statement.read",
  bankStatementImport: "finance.bank-statement.import",
  bankStatementMatch: "finance.bank-statement.match",
  bankStatementReconcile: "finance.bank-statement.reconcile",

  // Fixed assets
  assetClassRead: "finance.asset-class.read",
  assetClassCreate: "finance.asset-class.create",
  assetClassUpdate: "finance.asset-class.update",
  fixedAssetRead: "finance.fixed-asset.read",
  fixedAssetCreate: "finance.fixed-asset.create",
  fixedAssetUpdate: "finance.fixed-asset.update",
  fixedAssetDispose: "finance.fixed-asset.dispose",
  depreciationRunRead: "finance.depreciation-run.read",
  depreciationRunExecute: "finance.depreciation-run.execute",

  // Period close
  periodCloseRead: "finance.period-close.read",
  recurringJournalRead: "finance.recurring-journal.read",
  recurringJournalMaterialize: "finance.recurring-journal.materialize",
  recurringJournalCreate: "finance.recurring-journal.create",
  recurringJournalUpdate: "finance.recurring-journal.update",
  fxRevaluationRead: "finance.fx-revaluation.read",
  fxRevaluationExecute: "finance.fx-revaluation.execute",
  periodCloseRunChecks: "finance.period-close.run-checks",
  periodCloseSignOff: "finance.period-close.sign-off",
  periodCloseTransition: "finance.period-close.transition",
  periodCloseReopen: "finance.period-close.reopen",
  yearEndCloseRead: "finance.year-end-close.read",
  yearEndCloseExecute: "finance.year-end-close.execute",

  // Payment gateway
  gatewayPaymentRead: "finance.gateway-payment.read",
  gatewayPaymentResolve: "finance.gateway-payment.resolve",
  gatewayPaymentIngest: "finance.gateway-payment.ingest",

  // Cross-domain reads finance screens depend on for their reference pickers.
  ledgerAccountRead: "ledger.account.read",
  ledgerFiscalPeriodRead: "ledger.fiscal-period.read",
  ledgerFiscalCalendarRead: "ledger.fiscal-calendar.read",
  partyRead: "partners.party.read",
} as const;

export type FinancePermission = (typeof FINANCE_PERMISSIONS)[keyof typeof FINANCE_PERMISSIONS];

/** Direct-route authorization: a denied page never mounts and never queries. */
export function FinancePermissionBoundary({
  permission,
  children,
}: {
  permission: FinancePermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  const app = appOwningPermission(permission);
  if (!hasModule(app)) return <ModuleBoundary app={app}>{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="finance-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="finance-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-xs font-semibold tracking-[0.18em] text-destructive">
              {t("finance.permissions.code", "403 · ACCESS CONTROL")}
            </p>
            <h1 id="finance-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("finance.permissions.title", "Finance access restricted")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Which app owns a finance permission.
 *
 * Finance used to be one app holding a quarter of the ERP; it is now five, and
 * six of its areas moved to Accounting. The screens did not move, so the owner
 * is read off the permission the boundary is already given rather than
 * threaded through every route as a second prop that could disagree with it.
 *
 * The sub-domain is the second dotted segment: `finance.bank-rule.read` is
 * Banking. An unrecognised one falls back to Accounting, which is the app
 * every finance screen ultimately posts through — a wrong-but-installed answer
 * beats refusing a screen because this table has not heard of it yet.
 */
const PERMISSION_SUBDOMAIN_TO_APP: Record<string, AppCode> = {
  "bank-account": "banking",
  "bank-statement": "banking",
  "bank-rule": "banking",
  "customer-invoice": "receivables",
  "customer-receipt": "receivables",
  "credit-note": "receivables",
  "customer-statement": "receivables",
  dunning: "receivables",
  "ar-aging": "receivables",
  "gateway-payment": "receivables",
  "supplier-invoice": "payables",
  "payment-run": "payables",
  "ap-aging": "payables",
  grni: "payables",
  "fixed-asset": "fixed_assets",
  "asset-class": "fixed_assets",
  "depreciation-run": "fixed_assets",
  "tax-code": "tax",
  "tax-return": "tax",
  "period-close": "accounting",
  "year-end-close": "accounting",
  statement: "accounting",
  "statement-layout": "accounting",
  "recurring-journal": "accounting",
  "fx-revaluation": "accounting",
};

export function appOwningPermission(permission: string): AppCode {
  const subdomain = permission.split(".")[1] ?? "";
  return PERMISSION_SUBDOMAIN_TO_APP[subdomain] ?? "accounting";
}
